import type { ShippingForRouting } from '@/pages/Business/pages/Shippings/models/routing.types';
import type { RouteMonitoring } from '../models';
import type { RouteVisitShippingFilters } from '../hooks';
import { VisitStatus } from '../enums';
import VisitList from './VisitList';
import VisitShippingFilters from './VisitShippingFilters';
import { useMemo } from 'react';

interface VisitsPanelProps {
  routeDetail: RouteMonitoring;
  statusFilter: VisitStatus | null;
  shippingFilters: RouteVisitShippingFilters;
  enrichedByNumber: Map<number, ShippingForRouting>;
  onVisitMapFocus: (visitLatitude: number, visitLongitude: number) => void;
  syncVisitSearchFromRouteShippingNumber?: string;
}

const VisitsPanel = ({
  routeDetail,
  statusFilter,
  shippingFilters,
  enrichedByNumber,
  onVisitMapFocus,
  syncVisitSearchFromRouteShippingNumber,
}: VisitsPanelProps) => {
  const totalPackages = useMemo(
    () =>
      routeDetail.visits.reduce(
        (sum, visit) => sum + (visit.packagesCount || 0),
        0
      ),
    [routeDetail]
  );

  const visits = shippingFilters.filterVisits(
    statusFilter
      ? routeDetail.visits.filter((v) => v.status === statusFilter)
      : routeDetail.visits
  );

  // z-10 alcanza para quedar sobre el mapa (su contenedor es z-0 y encierra los
  // z-index de Leaflet) y deja el panel debajo del sidebar (20) y del header
  // sticky (30), que antes le tapaba con z-[1000].
  return (
    <div className='relative z-10 isolate flex flex-1 flex-col bg-white p-6 rounded-lg shadow-md'>
      <div className='mb-4 flex items-center justify-between shrink-0'>
        <h3 className='text-xl font-bold text-gray-800'>Visitas</h3>
        {shippingFilters.isActive ? (
          <span className='text-sm font-medium text-gray-500'>
            <span className='text-base font-bold text-gray-800 tabular-nums'>
              {shippingFilters.matchingCount}
            </span>{' '}
            de {shippingFilters.totalCount}{' '}
            {shippingFilters.totalCount === 1 ? 'pedido' : 'pedidos'}
          </span>
        ) : (
          <span className='text-sm font-medium text-gray-500'>
            {routeDetail.visits.length}{' '}
            {routeDetail.visits.length === 1 ? 'visita' : 'visitas'} ·{' '}
            {totalPackages > 0
              ? `${totalPackages} ${totalPackages === 1 ? 'bulto' : 'bultos'} en total`
              : 'No hay bultos registrados'}
          </span>
        )}
      </div>
      <VisitShippingFilters filters={shippingFilters} />
      <div className='min-h-0 flex-1 max-h-[min(80vh,800px)] overflow-y-auto overflow-x-hidden pr-1'>
        <VisitList
          visits={visits}
          routeMonitoring={routeDetail}
          shippingFilters={shippingFilters}
          enrichedByNumber={enrichedByNumber}
          onVisitMapFocus={onVisitMapFocus}
          syncSearchFromRouteShippingNumber={
            syncVisitSearchFromRouteShippingNumber
          }
        />
      </div>
    </div>
  );
};

export default VisitsPanel;
